import type { ReactNode } from 'react';
import { Breadcrumb, Tabs, Tooltip } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import PlannerTab from './PlannerTab';
import AddFilev2 from 'assets/svg/AddFilev2';
import { PlanDetailsProvider, usePlanDetailsContext } from './context/PlanDetailsContext';
import { useTpoGenConfig } from '../context/CustomerConfigContext';
import { FieldRulesProvider } from '../rules/store/FieldRulesProvider';

const BreadCrumbComponent = ({ planName }: { planName: string }) => {
    const tpoGenConfig = useTpoGenConfig();
    const { currentPlanDetails, hasUnsavedNoPromoEvent } = usePlanDetailsContext();
    const { state } = useLocation();
    const plansIndexPath = `/tpo-gen/plans-index${state?.fromSearch ?? ''}`;
    return (
        <div className="mb-3">
            <Breadcrumb>
                <Breadcrumb.Item>{tpoGenConfig.labels.common.breadcrumbRoot}</Breadcrumb.Item>
                <Breadcrumb.Item>
                    {hasUnsavedNoPromoEvent ? (
                        <Tooltip title={tpoGenConfig.labels.planDetail.exitWithoutPromoSave}>
                            <span className="text-[#A49FA9] cursor-not-allowed">
                                {tpoGenConfig.labels.planDetail.breadcrumb}
                            </span>
                        </Tooltip>
                    ) : (
                        <Link to={plansIndexPath}>{tpoGenConfig.labels.planDetail.breadcrumb}</Link>
                    )}
                </Breadcrumb.Item>
                {currentPlanDetails?.planning_group_name && (
                    <Breadcrumb.Item>
                        {hasUnsavedNoPromoEvent ? (
                            <Tooltip title={tpoGenConfig.labels.planDetail.exitWithoutPromoSave}>
                                <span className="text-[#A49FA9] cursor-not-allowed">
                                    {currentPlanDetails.planning_group_name}
                                </span>
                            </Tooltip>
                        ) : (
                            <Link to={plansIndexPath} className="text-sm text-[#18181B] truncate max-w-[200px]">
                                {currentPlanDetails.planning_group_name}
                            </Link>
                        )}
                    </Breadcrumb.Item>
                )}
                <Breadcrumb.Item>
                    <p className="text-sm text-[#18181B] truncate max-w-[200px]">
                        {currentPlanDetails?.plan_name || planName}
                    </p>
                </Breadcrumb.Item>
            </Breadcrumb>
        </div>
    );
};

const PlanDetailsHeaderComponent = () => {
    const tpoGenConfig = useTpoGenConfig();
    const { currentPlanDetails } = usePlanDetailsContext();
    return (
        <div>
            <p className="text-sm text-[#A49FA9] flex flex-wrap">
                <span className="font-semibold">{tpoGenConfig.labels.planDetail.customer}</span>{' '}
                {currentPlanDetails?.dim2_attrs.join(', ')}{' '}
                <span className="font-semibold ml-2">{tpoGenConfig.labels.planDetail.category}</span>{' '}
                {currentPlanDetails?.dim1_attrs.join(', ')}{' '}
                <span className="font-semibold ml-2">{tpoGenConfig.labels.planDetail.year}</span>{' '}
                {currentPlanDetails?.plan_year}
            </p>
        </div>
    );
};

const PlanDetailsTabComponent = ({
    planName,
    planId,
    navState,
}: {
    planName: string;
    planId: undefined | string;
    navState: any;
}) => {
    const tpoGenConfig = useTpoGenConfig();
    const { currentPlanDetails } = usePlanDetailsContext();
    return (
        <div className="card-container">
            <Tabs
                items={[
                    {
                        label: (
                            <p className="text-sm text-[#18181B] truncate max-w-[200px]">
                                {currentPlanDetails?.plan_name || planName}
                            </p>
                        ),
                        key: planId ?? '',
                        children: (
                            <PlannerTab
                                planDetails={{
                                    planId: navState?.planId || planId || currentPlanDetails?.plan_id,
                                    planName: currentPlanDetails?.plan_name || navState?.planName,
                                }}
                            />
                        ),
                    },
                    {
                        label: (
                            <div className="flex items-center gap-2">
                                <AddFilev2 />
                                <span className="text-sm font-normal text-[#18181B] ">
                                    {tpoGenConfig.labels.planDetail.createNewPlan}
                                </span>
                            </div>
                        ),
                        key: 'create-new-plan',
                        children: <div>{tpoGenConfig.labels.planDetail.createNewPlan}</div>,
                        disabled: true,
                    },
                ]}
                type="card"
            />
        </div>
    );
};

// Provides the per-session field-rules store, sourced from useTpoGenConfig().fieldRules.
const FieldRulesGate = ({ children }: { children: ReactNode }) => {
    const { fieldRules } = useTpoGenConfig();
    return <FieldRulesProvider rules={fieldRules}>{children}</FieldRulesProvider>;
};

const PlanDetails = () => {
    const { state, pathname } = useLocation();
    const planName = state?.planName;
    const planId = pathname.split('/').pop();
    return (
        <PlanDetailsProvider key={planId}>
            <FieldRulesGate>
                <div>
                    <div className="flex flex-row justify-between">
                        <BreadCrumbComponent planName={planName || planId} />
                        <PlanDetailsHeaderComponent />
                    </div>
                    <PlanDetailsTabComponent planName={planName || planId} planId={planId} navState={state} />
                </div>
            </FieldRulesGate>
        </PlanDetailsProvider>
    );
};

export default PlanDetails;
